import React, { useState, useMemo, useEffect } from "react";
import { Badge } from "reactstrap";
import ReactTable from "components/ReactTable/ReactTable.js";
import ReactTable2 from "components/ReactTable/ReactTable2.js";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { useGetTenants } from "api/tenant/service";
import { formatPriceWithDollar } from "utils/currency";
import { useGetAllSACalculus } from "api/super-admin/service";
import classNames from "classnames";
import AccountsFilters from "../../accounts/components/AccountsFilters";
import AccountsCards from "../../accounts/components/AccountsCards";

const getStatusOptions = (t) => [
  { value: "active", label: t("accountStatus.active") },
  { value: "bankruptcy", label: t("accountStatus.bankruptcy") },
  { value: "closed", label: t("accountStatus.closed") },
  { value: "deleted", label: t("accountStatus.deleted") },
  { value: "inactive", label: t("accountStatus.inactive") },
  { value: "out_for_repo", label: t("accountStatus.outForRepo") },
  { value: "repossessed", label: t("accountStatus.repossessed") },
  { value: "skip", label: t("accountStatus.skip") },
  { value: "transferred", label: t("accountStatus.transferred") },
  { value: "charge_off", label: t("accountStatus.chargeOff") },
];

const Accounts = ({ activeColor = "blue", shellThemeVariant = "1" }) => {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState([]);
  const [page, setPage] = useState(1);
  const [pageSize, setPageSize] = useState(20);
  const [tenant, setTenant] = useState("");
  const [debouncedSearch, setDebouncedSearch] = useState("");
  const { data: tenantsData, isLoading: isLoadingTenants } = useGetTenants(1, 1000);

  const statusArray = useMemo(() => status.map((option) => option.value), [status]);

  useEffect(() => {
    const handler = setTimeout(() => setDebouncedSearch(search), 300);
    return () => clearTimeout(handler);
  }, [search]);

  const { data: calculuses, isLoading } = useGetAllSACalculus(
    debouncedSearch,
    statusArray,
    page,
    pageSize,
    tenant
  );

  const serverPageCount = calculuses?.pagination?.pageCount;
  useEffect(() => {
    if (serverPageCount == null || serverPageCount < 1) return;
    if (page > serverPageCount) {
      setPage(serverPageCount);
    }
  }, [serverPageCount, page]);

  const isTheme2 = shellThemeVariant === "2";
  const AccountsTable = isTheme2 ? ReactTable2 : ReactTable;

  const statusOptionsData = useMemo(() => getStatusOptions(t), [t]);

  const tenantOptions = useMemo(
    () =>
      (tenantsData?.data || tenantsData?.results || []).map((row) => ({
        value: String(row.code),
        label: row.name,
      })) || [],
    [tenantsData]
  );

  const tenantValue = useMemo(
    () =>
      tenant
        ? {
            value: tenant,
            label:
              tenantOptions.find((opt) => String(opt.value) === String(tenant))?.label || tenant,
          }
        : null,
    [tenant, tenantOptions]
  );

  const accountsData = useMemo(() => {
    const list = calculuses?.data || calculuses?.results || calculuses || [];
    const rows = Array.isArray(list) ? list : [];
    return rows.map((item) => {
      const acctStatus = item.act_status || "";
      const daysPd = item.days_pd ?? 0;
      return {
        tenant_name: item.tenant?.name || "-",
        tenant_id: item.tenant?.id || "",
        borrower_name: item.customer?.full_name || "",
        booking_id: item.booking_id || "",
        stk_number: item.inventory?.stock_number || "",
        vin: item.inventory?.vin || "",
        acct_status: acctStatus,
        days_pd: daysPd,
        total_balance: item.total_balance ? formatPriceWithDollar(item.total_balance) : "",
      };
    });
  }, [calculuses]);

  const accountColumns = useMemo(
    () => [
      { Header: t("searchInventory.tenant"), accessor: "tenant_name" },
      { Header: t("accounts.borrowerName"), accessor: "borrower_name" },
      {
        Header: t("accounts.acct"),
        accessor: "booking_id",
        headerClassName: "text-center",
        className: "text-center",
      },
      {
        Header: t("accounts.stkNumber"),
        accessor: "stk_number",
        headerClassName: "text-center",
        className: "text-center",
        Cell: ({ row }) => (
          <div className="text-center">
            <div>{row.original.stk_number}</div>
            <div style={{ fontSize: "0.85em", opacity: 0.7 }}>{row.original.vin}</div>
          </div>
        ),
      },
      {
        Header: t("accounts.acctStatus"),
        accessor: "acct_status",
        headerClassName: "text-center",
        className: "text-center",
        Cell: ({ value }) => {
          const statusText = value || "-";
          const isActive = String(statusText).toLowerCase() === "active";
          return (
            <span
              style={{
                color: isActive ? "#28a745" : "inherit",
                fontWeight: isActive ? "bold" : "normal",
              }}
            >
              {statusText}
            </span>
          );
        },
      },
      {
        Header: t("accounts.daysPd", "Days PD"),
        accessor: "days_pd",
        headerClassName: "text-center",
        className: "text-center",
        Cell: ({ value }) => (
          <Badge
            color={Number(value) > 30 ? "danger" : Number(value) > 0 ? "warning" : "success"}
          >
            {value}
          </Badge>
        ),
      },
      {
        Header: t("accounts.totalBalance"),
        accessor: "total_balance",
        headerClassName: "text-right",
        className: "text-right",
      },
    ],
    [t]
  );

  const filters = (
    <AccountsFilters
      activeColor={activeColor}
      isTheme2={isTheme2}
      search={search}
      onSearchChange={setSearch}
      status={status}
      onStatusChange={setStatus}
      accountType=""
      onAccountTypeChange={() => {}}
      locations={[]}
      onLocationsChange={() => {}}
      statusOptions={statusOptionsData}
      accountTypeOptions={[]}
      showAccountTypeFilter={false}
      showLocationFilters={false}
      showTenantFilter
      onTenantChange={(value) => {
        setTenant(value);
        setPage(1);
      }}
      tenantValue={tenantValue}
      tenantOptions={tenantOptions}
      isLoadingTenants={isLoadingTenants}
      tenantLabel={t("searchInventory.tenant")}
    />
  );

  const table = (
    <div
      className="inventory-table-wrap"
      style={{ fontSize: isTheme2 ? "13px" : "12px", minWidth: 0, maxWidth: "100%" }}
    >
      <AccountsTable
        activeColor={activeColor}
        data={accountsData}
        columns={accountColumns}
        className={classNames("-striped -highlight", { "sticky-stock-table": isTheme2 })}
        manualPagination={true}
        pageCount={calculuses?.pagination?.pageCount || 1}
        pageIndex={page - 1}
        totalItems={calculuses?.pagination?.total}
        pageSize={pageSize}
        noDataText={t("accounts.noRecordsFound")}
        loading={isLoading}
        loadingText={t("accounts.loading")}
        onPageChange={(pageIndex) => setPage(pageIndex + 1)}
        onPageSizeChange={(size) => {
          setPageSize(size);
          setPage(1);
        }}
        onRowClick={(row) => {
          const tenantQuery = row.tenant_id ? `?tenantId=${row.tenant_id}` : "";
          navigate(`/accounts/${row.booking_id}${tenantQuery}`);
        }}
      />
    </div>
  );

  return (
    <div
      className={classNames("content", "search-accounts", "search-accounts--sa", {
        "search-accounts--theme2": isTheme2,
        "theme2-admin-content-compact": isTheme2,
        "theme2-accounts-inset": isTheme2,
      })}
    >
      <AccountsCards
        isTheme2={isTheme2}
        filters={filters}
        table={table}
        title={`Super Admin - ${t("accounts.title")}`}
      />
    </div>
  );
};

export default Accounts;
